import { IconCertificate } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Focusable, Tooltip } from '@cfa/react-core';

import { cn } from '@/utils/tailwind';

import { PrintCertificateModals } from './PrintCertificateModals';
import { usePrintCertificates } from './usePrintCertificates';

export interface PrintCertificatesProps {
  planId?: string;
  teamMemberId?: string;
  className?: string;
}

/**
 * Print certificate button that encapsulates the show/hide logic
 * and provides a consistent interface across Reports views.
 *
 * Shows only for users with Leader or Operator (or above) permissions.
 *
 * For use inside Header.Root, use `usePrintCertificates` hook instead
 * and render Header.ActionIconButton directly.
 */
const PrintCertificates: React.FC<PrintCertificatesProps> = props => {
  const { className, planId, teamMemberId } = props;

  const { t } = useTranslation();
  const certificateHook = usePrintCertificates({
    planId,
    teamMemberId,
  });

  if (!certificateHook.shouldShow) {
    return null;
  }

  const tooltipKey = teamMemberId
    ? 'Button.getCertificate'
    : 'Button.getCertificates';

  return (
    <>
      <Tooltip.Root delay={500}>
        <Focusable>
          <button
            className={cn(
              'pointer-events-auto! border-none bg-transparent p-0',
              certificateHook.isLoading
                ? 'text-grayscale-gray3! cursor-not-allowed'
                : 'text-secondary-base! cursor-pointer',
              className,
            )}
            data-testid="certificate-icon"
            disabled={certificateHook.isLoading}
            onClick={certificateHook.handleClick}
          >
            <IconCertificate />
          </button>
        </Focusable>
        <Tooltip.Content>{t(tooltipKey)}</Tooltip.Content>
      </Tooltip.Root>
      <PrintCertificateModals {...certificateHook} />
    </>
  );
};

export default PrintCertificates;
